import { Check, FileText, Loader2, RotateCcw, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { fillLetter } from '@jt/domain';
import { ContractDocument } from './ContractDocument';

/**
 * A document, read here and edited here.
 *
 * Used by all three kinds — the customer's service agreement, the employment
 * letter, and the landlord's building access agreement — so that "how do I
 * change the wording?" has one answer everywhere.
 *
 * ## Why this lives on the document and not in a form below it
 *
 * It used to be a small link inside "The terms, and our signature", underneath
 * two dozen fields. Everything worked and nobody could find it: you open a
 * document, you are looking at the words, and the place you expect to change
 * them is the thing you are looking at. An edit control anywhere else is an
 * edit control that does not exist.
 *
 * ## Why a plain textarea and not a rich editor
 *
 * What is stored is what is hashed and what is printed. A rich editor puts a
 * layer of its own markup between the person typing and the thing a landlord
 * signs, and the day those two disagree is the day a signature stops meaning
 * anything. Markdown in, markdown out, and Preview renders exactly what the
 * document will render.
 *
 * The `{{placeholders}}` stay visible while editing on purpose: somebody
 * changing clause 12 should see `{{exclusivityDescription}}` and know to leave
 * it alone, rather than see the filled-in sentence and wonder why their edit
 * keeps reverting.
 */
export function DocumentEditor({
  heading,
  body,
  values,
  canEdit,
  saving,
  onSave,
  note,
  standard,
  blurb,
  slots,
  leaveOpen,
}: {
  /** What this document is called on screen — "The agreement", "The letter". */
  heading: string;
  body: string;
  values: Record<string, unknown>;
  canEdit: boolean;
  saving: boolean;
  /**
   * Resolves when the save has landed, which is what closes the editor.
   *
   * A promise rather than a callback, because the editor cannot close
   * optimistically: a refused save that left the textarea gone would take the
   * unsaved wording with it.
   */
  onSave: (body: string) => Promise<unknown>;
  /** Shown while editing — what changing this will mean for this document. */
  note?: string;
  /**
   * The wording a fresh document of this kind would carry, when there is one.
   *
   * Only some documents have a canonical version to go back to: an agreement
   * and an employment letter are generated from a known text, while a customer
   * contract comes from whichever template was chosen. No standard, no button
   * — offering to "restore" something that was never standard would be a lie.
   */
  standard?: string;
  /** A sentence under the heading, when the document warrants one. */
  blurb?: string;
  /**
   * Gaps to leave open while reading, and what to put in them.
   *
   * This is the SignEasy half: the person whose turn it is types their answers
   * into the clauses that ask for them, rather than into a grid of boxes
   * underneath the document with no sight of the sentences they belong to.
   *
   * Only the reading view takes them. Editing is about the wording, and the
   * placeholders stay visible as `{{braces}}` there on purpose — somebody
   * rewriting clause 12 needs to see which parts are filled in later and leave
   * them alone. Preview follows editing rather than reading, for the same
   * reason: it exists to show what the wording will look like, and an input in
   * it would invite somebody to type into a preview and lose it.
   */
  slots?: Record<string, ReactNode>;
  leaveOpen?: ReadonlySet<string>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(body);
  const [preview, setPreview] = useState(false);

  const open = () => {
    setDraft(body);
    setPreview(false);
    setEditing(true);
  };

  if (!editing) {
    return (
      <section className="card print:hidden">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-heading text-lg">{heading}</h2>
          {canEdit && (
            <button type="button" className="btn-secondary" onClick={open}>
              <FileText className="h-4 w-4" aria-hidden="true" />
              Edit {heading.replace(/^The /i, 'the ')}
            </button>
          )}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {blurb ?? 'The gaps fill in as they are typed, so what is read is what is signed.'}
          {canEdit && ' Every word of it can be changed.'}
        </p>
        {/*
          * The scroll box goes away while there is filling to do.
          *
          * A document scrolling inside a fixed-height box moves its gaps under
          * a signing bar that is sticky to the window, so "Next blank" would
          * scroll the wrong thing. It is the right container for reading and
          * the wrong one for writing.
          */}
        <div
          className={`mt-2 rounded-xl border border-border bg-background p-4 text-sm ${
            slots ? '' : 'max-h-[28rem] overflow-y-auto'
          }`}
        >
          <ContractDocument body={fillLetter(body, values, leaveOpen)} slots={slots} />
        </div>
      </section>
    );
  }

  return (
    <section className="card space-y-3 print:hidden">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-heading text-lg">
          Editing {heading.replace(/^The /i, 'the ')}
        </h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => setPreview((on) => !on)}
            aria-pressed={preview}
          >
            {preview ? 'Back to editing' : 'Preview'}
          </button>
          {standard !== undefined && draft.trim() !== standard.trim() && (
            <button
              type="button"
              className="btn-ghost text-sm text-muted-foreground"
              onClick={() => {
                if (
                  window.confirm(
                    'Put the standard wording back?\n\nEverything typed into this document ' +
                      'is lost. The details filled in around it — names, figures, dates — are ' +
                      'not touched.',
                  )
                ) {
                  setDraft(standard);
                }
              }}
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
              Standard wording
            </button>
          )}
        </div>
      </div>

      {note && <p className="rounded-xl bg-accent-50 p-3 text-sm text-accent-900">{note}</p>}

      <p className="text-xs text-muted-foreground">
        Markdown. Anything in double braces —{' '}
        <code className="rounded bg-muted px-1">{'{{propertyName}}'}</code> — is filled in when the
        agreement is read, so leave those in place.
      </p>

      {preview ? (
        <div className="max-h-[32rem] overflow-y-auto rounded-xl border border-border bg-background p-4 text-sm">
          <ContractDocument body={fillLetter(draft, values)} />
        </div>
      ) : (
        <textarea
          className="input !min-h-[34rem] w-full font-mono text-xs leading-relaxed"
          /*
           * `!` because `textarea.input` in index.css sets min-h-20, and an
           * element+class selector beats a utility class on specificity — the
           * cascade does not care which layer they came from. Without it the
           * whole agreement is edited through a three-line box.
           */
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          spellCheck={false}
          aria-label="The agreement, as Markdown"
        />
      )}

      <div className="flex flex-wrap gap-2 border-t border-border pt-3">
        <button
          type="button"
          className="btn-primary"
          disabled={saving || draft.trim().length < 50}
          onClick={() => {
            void onSave(draft).then(
              () => setEditing(false),
              // Stay open on a refusal, so nothing typed is lost.
              () => undefined,
            );
          }}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Check className="h-4 w-4" aria-hidden="true" />
          )}
          Save {heading.replace(/^The /i, 'the ')}
        </button>
        <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>
          <X className="h-4 w-4" aria-hidden="true" />
          Cancel
        </button>
      </div>
    </section>
  );
}
